<!-- TEMPLATE: AddInvitationView.vue (Edit Invitation) -->
<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">

        <!-- STEP 1: Event Type Picker (shown only if API didn't specify an event type) -->
        <div v-if="requiresEventTypeChoice && step==='type'">
          <div class="row align-items-center justify-items-between mb-3 w-100">
            <div class="col-8"><h4 class="mb-0">Host an Event</h4></div>
            <div class="col-4 text-right">
              <BackButton class="btn btn-dark btn-sm py-1 px-3" fallback="/host">
                <i class="fas fa-chevron-left mr-2"></i>BACK
              </BackButton>
            </div>
          </div>

          <div class="page-bar py-3 mb-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <button class="btn btn-link p-0 mr-2" @click="step='type'">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="page-title flex-grow-1 text-center">{{ (selectedEventType && selectedEventType.title) ? selectedEventType.title : 'Create Event' }}</div>
              </div>
            </div>
          </div>

          <p class="font-weight-semibold mb-3">Tell us about the event mode</p>

          <div v-if="loadingEventTypes" class="text-center py-5"><div class="spinner-border"></div></div>

          <div v-else>
            <button
              v-for="et in eventTypes"
              :key="et.id"
              type="button"
              class="event-type-tile w-100 text-left mb-3"
              :class="{active: selectedEventType && selectedEventType.id===et.id}"
              @click="selectEventType(et)"
            >
              <div class="event-type-tile__body">
                <div class="event-type-tile__copy">
                  <div class="h5 mb-1">{{ et.title }}</div>
                  <div class="small text-muted">{{ et.description }}</div>
                </div>
                <span class="radio-badge">
                  <span class="dot" v-if="selectedEventType && selectedEventType.id===et.id"></span>
                </span>
              </div>
            </button>

            <div class="text-center mt-4">
              <button class="btn btn-primary btn-lg px-4 chip-btn" :disabled="!selectedEventType" @click="goToForm">
                Proceed to Next
              </button>
            </div>
          </div>
        </div>

        <!-- STEP 2: Edit Form -->
        <div v-else> 

          <div class="page-bar py-3 mb-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1 text-center">{{ formTitle }}</div>
              </div>
            </div>
          </div>

          <div class="card">
            <form @submit.prevent="submitForm">
              <div class="card-body">

                <!-- Event Title -->
                <div class="form-group mb-3">
                  <input id="eventTitle" type="text" class="form-control" placeholder="Enter event title" v-model.trim="form.title" required />
                </div>

                <!-- Choose Cover Media -->
                <div class="mb-3">
                  <CoverMediaPicker v-model="coverMedia" mode="edit" />
                </div>

                <!-- Set A Date -->
                <div class="mb-3">
                  <button type="button" class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center chip-btn" @click="toggleCollapse('dateCollapse')">
                    <span><i class="fas fa-calendar mr-2"></i> Set A Date</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.dateCollapse,'fa-chevron-up': collapses.dateCollapse}"></i>
                  </button>

                  <div class="collapse mt-2" :class="{ show: collapses.dateCollapse }" id="dateCollapse">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span>All-day</span>
                      <label class="switch mb-0">
                        <input type="checkbox" v-model="all_day" aria-label="Toggle all day" />
                        <span class="slider round"></span>
                      </label>
                    </div>
                    <div class="d-flex align-items-center justify-content-between mb-3">
                      <span>Include End Time</span>
                      <label class="switch mb-0">
                        <input type="checkbox" v-model="include_end" aria-label="Toggle include end time" />
                        <span class="slider round"></span>
                      </label>
                    </div>

                    <!-- Starts -->
                    <div class="form-group">
                      <label for="startDateInput">Starts:</label>
                      <div class="row flex-wrap align-items-center">
                        <div class="col-md-8 col-7">
                          <input id="startDateInput" type="text" class="form-control mr-2 mb-2" v-model="start_date" :disabled="isEventPastLocked" required />
                        </div>
                        <div class="col-md-4 col-5" v-if="!all_day">
                          <input id="startTimeInput" type="text" class="form-control mb-2" v-model="start_time" :disabled="isEventPastLocked" />
                        </div>
                      </div>
                    </div>

                    <!-- Ends -->
                    <div class="form-group" v-if="include_end">
                      <label for="endDateInput">Ends:</label>
                      <div class="row flex-wrap align-items-center">
                        <div class="col-md-8 col-7">
                          <input id="endDateInput" type="text" class="form-control mr-2 mb-2" v-model="end_date" :disabled="isEventPastLocked" required />
                        </div>
                        <div class="col-md-4 col-5" v-if="!all_day">
                          <input id="endTimeInput" type="text" class="form-control mb-2" v-model="end_time" :disabled="isEventPastLocked" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Location -->
                <div class="mb-3">
                  <button type="button" class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center chip-btn" @click="toggleCollapse('locationCollapse')">
                    <span><i class="fas fa-map-marker-alt mr-2"></i> Location</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.locationCollapse,'fa-chevron-up': collapses.locationCollapse}"></i>
                  </button>
                  <div class="collapse mt-2" :class="{ show: collapses.locationCollapse }" id="locationCollapse">
                    <div class="form-group">
                      <label for="locationSearch" class="form-label">Search Location</label>
                      <input id="locationSearch" type="text" class="form-control" placeholder="Enter location" v-model="locationSearch" @input="onLocationSearch"/>
                      <div v-if="locationSuggestions.length>0" class="list-group mt-2">
                        <button type="button" v-for="(suggestion, index) in locationSuggestions" :key="index" class="list-group-item list-group-item-action" @click="selectLocation(suggestion)">
                          {{ suggestion.description }}
                        </button>
                      </div>
                    </div>

                    <div class="row justify-content-between text-center py-3 chip-actions">
                      <div class="col-md-6 col-6 mb-2 mb-md-0">
                        <button class="btn btn-primary address-chip chip-btn w-100" type="button" @click="getCurrentLocation" :disabled="gettingLocation">
                          <span v-if="gettingLocation" class="spinner-border spinner-border-sm mr-2"></span>
                          <i v-else class="fas fa-location-arrow mr-2"></i>
                          {{ gettingLocation ? 'Getting Location...' : 'Use My Current Location' }}
                        </button>
                      </div>
                      <div class="col-md-6 col-6">
                        <button class="btn btn-outline-primary address-chip chip-btn w-100" type="button" @click="showManualAddress=!showManualAddress">
                          <i class="fas fa-plus-circle mr-2"></i>
                          {{ showManualAddress ? 'Hide Address' : 'Add Address Manually' }}
                        </button>
                      </div>
                    </div>

                    <div v-if="showManualAddress" class="manual-address-fields">
                      <div class="form-group">
                        <label for="eventPlace" class="form-label">Event Place</label>
                        <input id="eventPlace" type="text" class="form-control" placeholder="Enter event place" v-model.trim="form.event_place"/>
                      </div>
                      <div class="form-group">
                        <label for="doorNo" class="form-label">Flat / H.No / Floor / Building</label>
                        <input id="doorNo" type="text" class="form-control" placeholder="Enter details" v-model.trim="form.door_no"/>
                      </div>
                      <div class="form-group">
                        <label for="locality" class="form-label">Area / Sector / Locality</label>
                        <input id="locality" type="text" class="form-control" placeholder="Enter area/locality" v-model.trim="form.locality"/>
                      </div>
                    </div>

                    <input type="hidden" v-model.trim="form.lat">
                    <input type="hidden" v-model.trim="form.lng">
                  </div>
                </div>

                <!-- Hosted By -->
                <div class="mb-3">
                  <button type="button" class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center chip-btn" @click="toggleCollapse('hostedCollapse')">
                    <span><i class="fas fa-user-tie mr-2"></i> Hosted By</span>
                    <i class="fas" :class="{'fa-chevron-down': !collapses.hostedCollapse,'fa-chevron-up': collapses.hostedCollapse}"></i>
                  </button>

                  <div class="collapse mt-2" :class="{ show: collapses.hostedCollapse }" id="hostedCollapse">
                    <div class="form-group">
                      <label class="form-label">Name</label>
                      <input type="text" class="form-control" v-model="hostNameDisplay">
                    </div>

                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <label class="mb-0">Co-hosts</label>
                      <button type="button" class="btn btn-outline-primary chip-btn" @click="openCohostModal">Add/Manage Co-hosts</button>
                    </div>

                    <!-- Selected Chips -->
                    <div class="d-flex flex-wrap mb-3" v-if="selectedCohosts.length>0">
                      <div v-for="cohost in selectedCohosts" :key="cohost.id" class="cohost-chip rounded-pill d-flex align-items-center p-2 m-1">
                        <img v-no-right-click :src="cohost.user_img || 'https://dummyimage.com/200x200/222/fff.png'" class="rounded-circle mr-2" style="width:25px;height:25px;object-fit:cover;">
                        <span class="small">{{ capitalize(cohost.name) }}</span>
                        <button type="button" class="btn btn-sm btn-link text-danger p-0 ml-2" @click="removeCohost(cohost)">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <div class="form-group mb-3">
                  <textarea id="eventDescription" class="form-control" rows="5" placeholder="Add a detailed description of your event" v-model.trim="form.description"></textarea>
                </div>

                <div class="event-settings-card" @click="showSettingsModal=true">
                  <div>
                    <div class="font-weight-bold mb-1">Event Settings</div>
                    <div class="small text-muted">
                      Allow attendees to share comments and suggestions after the event to help improve future events.
                    </div>
                  </div>
                  <i class="fas fa-chevron-right text-muted"></i>
                </div>
              </div>

              <div class="card-footer text-center">
                <div class="d-grid d-flex justify-content-center gap-2 flex-wrap">
                  <button type="button" class="btn btn-outline-secondary btn-lg mx-2 chip-btn" style="min-width:150px" @click="cancelEdit">
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-primary btn-lg mx-2 chip-btn" style="min-width:150px" :disabled="isSubmitting">
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-2"></span>
                    {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Settings modal -->
  <div v-if="showSettingsModal" class="modal-backdrop-custom settings-backdrop" @click.self="showSettingsModal=false">
    <div class="settings-modal-card" role="dialog" aria-modal="true" tabindex="-1">
      <div class="settings-modal-header">
        <button class="btn btn-link p-0 mr-2 text-dark" @click="showSettingsModal=false" aria-label="Close settings">
          <i class="fas fa-chevron-left"></i>
        </button>
        <div class="flex-grow-1 text-center font-weight-bold">Event Settings</div>
        <div style="width:16px;"></div>
      </div>

      <div class="settings-modal-body settings-soft-panel">
        <div class="settings-section settings-soft-panel">
          <div class="settings-section-title">RSVP & Joining</div>
          <p class="settings-section-sub">Decide how people can participate</p>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Accept RSVP</div>
              <div class="settings-tile-sub">Guests can RSVP (Yes / No / Maybe) if enabled.</div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="settings.acceptRsvp" />
              <span class="slider round"></span>
            </label>
          </div>

          <div class="settings-tile" :class="{ 'disabled-tile': !settings.acceptRsvp }">
            <div>
              <div class="settings-tile-title">Guest Count (Adults/Children)</div>
              <div class="settings-tile-sub">Let guests set how many are coming.</div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="settings.guestCount" :disabled="!settings.acceptRsvp" />
              <span class="slider round"></span>
            </label>
          </div>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Enable Check-In</div>
              <div class="settings-tile-sub">Let guests check in when they arrive. Hosts can track attendance in real time.</div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="settings.checkinEnabled" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <div class="settings-section settings-soft-panel">
          <div class="settings-section-title">Album</div>
          <p class="settings-section-sub">Enable album to share event photos</p>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Album</div>
              <div class="settings-tile-sub">
                Capture and share memories effortlessly with our photo booth module, offering secure and customizable image and video sharing post-event.
              </div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="form.album_enabled" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <div class="settings-section settings-soft-panel">
          <div class="settings-section-title">Feedback</div>
          <p class="settings-section-sub">Let guests share how it went</p>

          <div class="settings-tile">
            <div>
              <div class="settings-tile-title">Enable Feedback</div>
              <div class="settings-tile-sub">
                Let your guests share their thoughts about the event. Enable this option to collect private feedback from attendees, helping you understand what they enjoyed and what could be improved for future events.
              </div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="form.feedback" />
              <span class="slider round"></span>
            </label>
          </div>

          <div class="settings-tile" :class="{'disabled-tile': !form.feedback}">
            <div>
              <div class="settings-tile-title">Enable Rating</div>
              <div class="settings-tile-sub">
                Enable guests to respond whether they will attend the event.
              </div>
            </div>
            <label class="switch mb-0">
              <input type="checkbox" v-model="form.rating" :disabled="!form.feedback" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <AttachmentOptions v-model="attachments" class="mb-3" />
      </div>
    </div>
  </div>

  <!-- Cohost Modal -->
  <div v-if="showCohostModal" class="modal-backdrop-custom" @click.self="closeCohostModal">
    <div class="modal-card">
      <div class="modal-header">
        <h5 class="modal-title">Select Co-hosts</h5>
        <button class="close" @click="closeCohostModal">&times;</button>
      </div>
      <div class="modal-body">
        <div v-if="cohosts.length===0" class="text-center py-4 text-muted">No friends found.</div>
        <ul class="list-group list-group-flush" v-else>
          <li class="list-group-item p-3" v-for="cohost in cohosts" :key="cohost.id">
            <div class="d-flex align-items-center">
              <img v-no-right-click :src="cohost.user_img || 'https://dummyimage.com/200x200/222/fff.png'" class="rounded-circle mr-3" style="width:44px;height:44px;object-fit:cover">
              <div class="flex-grow-1">
                <div class="font-weight-semibold">{{ capitalize(cohost.name) }}</div>
                <div class="small text-muted">@{{ cohost.username }}</div>
              </div>
              <button type="button" class="btn chip-btn" :class="isCohostSelected(cohost)?'btn-secondary':'btn-success'" @click="toggleCohost(cohost)">
                {{ isCohostSelected(cohost) ? 'Selected' : 'Select' }}
              </button>
            </div>
          </li>
        </ul>
      </div>
      <div class="modal-footer">
        <button class="btn btn-outline-secondary chip-btn" @click="closeCohostModal">Cancel</button>
        <button class="btn btn-primary chip-btn" @click="saveCohostModal">Done</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import api from "@/services/api";
import { useSessionStore } from "@/stores/session";
import Swal from "sweetalert2";
import "flatpickr/dist/flatpickr.css";
import BackButton from '@/components/common/BackButton.vue';
import AttachmentOptions from '@/components/common/AttachmentOptions.vue';
import CoverMediaPicker from '@/components/common/CoverMediaPicker.vue';

// Google Maps loader
const loadGoogleMapsAPI = () =>
  new Promise((resolve, reject) => {
    if (window.google && window.google.maps) return resolve();
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const t = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(t);
          resolve();
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "YOUR_API_KEY";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps API"));
    document.head.appendChild(script);
  });

// SweetAlert helpers
const toast = (msg, icon = "success") =>
  Swal.fire({ toast: true, position: "top-end", icon, title: msg, showConfirmButton: false, timer: 3000 });
const alertErr = (msg) => Swal.fire({ icon: "error", title: "Oops…", text: msg });

// Normalize "h:mm AM/PM"
const normalizeAmPm = (val) => {
  if (!val) return "";
  const s = String(val).trim().toUpperCase().replace(/\s+/g, " ");
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!m) return s;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const ampm = m[3];
  h = h % 12 || 12;
  return `${h}:${mm} ${ampm}`;
};

// --- Time helpers for constraints ---
function parseTimeToMinutes(str) {
  if (!str) return null;
  const s = String(str).trim().toUpperCase();
  const m = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3];
  if (ap) {
    const isPM = ap === 'PM';
    if (h === 12) h = isPM ? 12 : 0;
    else if (isPM) h += 12;
  }
  return (h * 60) + min;
}
function cmpTimes(a, b) {
  const A = parseTimeToMinutes(a), B = parseTimeToMinutes(b);
  if (A == null || B == null) return 0;
  return A - B;
}

export default {
  name: "EventEditForm",
  components: { BackButton, AttachmentOptions, CoverMediaPicker },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const session = useSessionStore();

    // eventid from route param
    const deriveEventId = (r) => {
      const cand = [r.params?.eventid, r.params?.id, r.params?.eventId, r.query?.eventid, r.query?.id, r.query?.eventId]
        .find((v) => v != null && String(v).trim() !== "");
      if (cand) return String(cand);
      const lastSeg = (r.path || r.fullPath || "").split("?")[0].split("/").filter(Boolean).pop();
      return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null;
    };
    const eventid = ref(deriveEventId(route));

    // Type step visibility
    const requiresEventTypeChoice = ref(false);

    // Step handling
    const step = ref("form");
    const eventTypes = ref([]);
    const selectedEventType = ref(null);
    const pendingEventTypeKey = ref("");
    const normalizedType = computed(() => {
      const current = selectedEventType.value?.type || "";
      const candidate = current || pendingEventTypeKey.value;
      return String(candidate || "").trim().toLowerCase();
    });
    const isNewEvent = computed(() => normalizedType.value === "new");
    const eventPastStatus = ref(null) // API flag
    const isEventPastLocked = computed(() => eventPastStatus.value === 1)
    const isPastEvent = computed(() =>
      isEventPastLocked.value ||
      normalizedType.value === "old" ||
      normalizedType.value === "past"
    );
    const loadingEventTypes = ref(true);

    const goToForm = () => (step.value = "form");
    const selectEventType = (et) => {
      selectedEventType.value = et;
      pendingEventTypeKey.value = String(et?.type || "").trim().toLowerCase();
    };

    const resolveEventTypeFromKey = () => {
      if (!pendingEventTypeKey.value || !eventTypes.value.length) return;
      const key = pendingEventTypeKey.value.trim().toLowerCase();
      const match = eventTypes.value.find((e) =>
        (e.type || e.key || "").toLowerCase() === key
      );
      if (match) {
        selectedEventType.value = match;
        pendingEventTypeKey.value = "";
      }
    };

    const fetchEventTypes = async () => {
      try {
        loadingEventTypes.value = true;
        const { data } = await api.getEventTypes();
        const list = data?.data || data?.list || [];
        eventTypes.value = list.length
          ? list
          : [
              { id: 1, title: "New Events", description: "Use this option to create a brand-new event.", type: "new" },
              { id: 2, title: "Past Events", description: "Select this option to view or edit an existing event.", type: "old" },
            ];
        resolveEventTypeFromKey();
      } catch (e) {
        console.error("Failed to fetch event types:", e);
        eventTypes.value = [
          { id: 1, title: "New Events", description: "Use this option to create a brand-new event.", type: "new" },
          { id: 2, title: "Past Events", description: "Select this option to view or edit an existing event.", type: "old" },
        ];
        resolveEventTypeFromKey();
      } finally {
        loadingEventTypes.value = false;
      }
    };

    // Form state
    const form = reactive({
      title: "",
      location: "",
      hosted_by: "",
      description: "",
      album_enabled: false,
      feedback: false,
      rating: false,
      images: [],
      lat: "",
      lng: "",
      event_place: "",
      door_no: "",
      locality: "",
      address: "",
    });

    const settings = reactive({
      acceptRsvp: true,
      guestCount: false,
      checkinEnabled: true,
    });

    const attachments = ref({
      textPhoto: false,
      schedule: false,
      poll: false,
      carPool: false
    });

    const showSettingsModal = ref(false);
    const toFlag = (val) => (val ? "1" : "0");
    const fetchUrlAsFile = async (url, fallbackName = "image") => {
      const src = String(url || "").trim();
      if (!src) return null;

      let res;
      try {
        res = await fetch(src, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          cache: "no-store"
        });
      } catch (err) {
        throw new Error("Remote image could not be downloaded. Check CORS for this source.");
      }

      if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);

      const blob = await res.blob();
      const contentType = String(blob.type || "").trim();
      if (!contentType.startsWith("image/")) {
        throw new Error("Remote asset did not return an image file.");
      }

      const extFromType = contentType.split("/")[1] || "jpg";
      let ext = `.${extFromType.replace("jpeg", "jpg")}`;
      try {
        const pathname = new URL(src).pathname || "";
        const match = pathname.match(/(\.[a-zA-Z0-9]{2,5})$/);
        if (match?.[1]) ext = match[1];
      } catch (_) {
        // ignore
      }

      return new File([blob], `${fallbackName}${ext || ".jpg"}`, {
        type: contentType || "image/jpeg"
      });
    };

    watch(
      () => settings.acceptRsvp,
      (val) => {
        if (!val) settings.guestCount = false;
      }
    );

    const formTitle = computed(() => selectedEventType.value?.title || "Edit Event");

    const collapses = reactive({
      dateCollapse: false,
      locationCollapse: false,
      hostedCollapse: false,
    });

    const isSubmitting = ref(false);
    const coverMedia = ref({ type: "images", images: [], video: null, youtube: null });

    // Dates & times
    const all_day = ref(false);
    const include_end = ref(true);
    const start_date = ref("");
    const start_time = ref("");
    const end_date = ref("");
    const end_time = ref("");

    // Location
    const locationSearch = ref("");
    const locationSuggestions = ref([]);
    const showManualAddress = ref(false);
    const gettingLocation = ref(false);
    const autocompleteService = ref(null);
    const placesService = ref(null);
    const geocoder = ref(null);

    // Cohosts
    const cohosts = ref([]);
    const selectedCohosts = ref([]);
    const showCohostModal = ref(false);
    const snapshotSelected = ref([]);

    // From API (ROOT) for preselecting cohosts
    const apiHostIdsCsv = ref("");
    const apiCohostNamesCsv = ref("");

    // Session / user
    const user = reactive({ id: "", name: "", username: "", email: "" });
    const hostNameDisplay = ref("");

    const fetchProfile = async () => {
      if (!session.userId) return;
      try {
        const fd = new FormData();
        fd.append("id", session.userId);
        const { data } = await api.getProfileApi(fd);
        if (data.status === "ok" && data?.data?.[0]) {
          Object.assign(user, data.data[0]);
          form.hosted_by = user.username || user.name || "";
          if (!hostNameDisplay.value) hostNameDisplay.value = user.name || user.username || "";
        }
      } catch(e) {
        console.error('Profile fetch error:', e);
      }
    };

    const toggleCollapse = (k) => (collapses[k] = !collapses[k]);

    const getCoHosts = async () => {
      try {
        const fd = new FormData();
        fd.append("userid", session.userId);
        const res = await api.getMyFriends(fd);
        const list = res?.data?.data || res?.data?.list || res?.data || [];
        cohosts.value = Array.isArray(list) ? list : [];
      } catch(e) {
        console.error('Error loading cohosts:', e);
      }
    };

    // Modal functions
    const openCohostModal = () => {
      snapshotSelected.value = [...selectedCohosts.value];
      showCohostModal.value = true;
    };
    const closeCohostModal = () => {
      selectedCohosts.value = [...snapshotSelected.value];
      showCohostModal.value = false;
    };
    const saveCohostModal = () => {
      snapshotSelected.value = [...selectedCohosts.value];
      showCohostModal.value = false;
      toast("Co-hosts updated successfully");
    };
    const toggleCohost = (cohost) => {
      const idx = selectedCohosts.value.findIndex((c) => c.id === cohost.id);
      if (idx > -1) selectedCohosts.value.splice(idx, 1);
      else selectedCohosts.value.push(cohost);
    };
    const removeCohost = (cohost) => {
      const idx = selectedCohosts.value.findIndex((c) => c.id === cohost.id);
      if (idx > -1) selectedCohosts.value.splice(idx, 1);
    };
    const isCohostSelected = (cohost) => selectedCohosts.value.some((c) => c.id === cohost.id);

    // Preselect cohosts from ROOT API
    const resolveCohostsFromApi = () => {
      if (!cohosts.value?.length) return;

      const idsCsv = apiHostIdsCsv.value;
      const namesCsv = apiCohostNamesCsv.value;

      if (idsCsv) {
        const ids = idsCsv.split(",").map((s) => s.trim()).filter(Boolean);
        selectedCohosts.value = cohosts.value.filter((c) => ids.includes(String(c.id)));
      }
      if (!selectedCohosts.value.length && namesCsv) {
        const want = namesCsv.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
        selectedCohosts.value = cohosts.value.filter((c) =>
          want.includes(String(c.name || "").trim().toLowerCase())
        );
      }
    };

    const ensureAmPm = (val) => {
      if (!val) return "";
      const s = String(val).trim();
      if (/[AP]M$/i.test(s)) return s.toUpperCase();
      const [hStr, m = "00"] = s.split(":");
      if (isNaN(+hStr)) return s;
      let h = parseInt(hStr, 10);
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${h}:${m} ${ampm}`;
    };

    const parseApiToggle = (val) => {
      if (val == null) return null;
      const normalized = String(val).trim().toLowerCase();
      if (!normalized) return null;
      if (["1", "true", "yes", "y", "on"].includes(normalized)) return true;
      if (["0", "false", "no", "n", "off"].includes(normalized)) return false;
      return null;
    };
    const moduleKeyMap = {
      text_photo: "textPhoto",
      schedule: "schedule",
      poll: "poll",
      car_pool: "carPool",
    };

    const loadInviteModules = async () => {
      if (!eventid.value) return;
      try {
        const fd = new FormData();
        fd.append("event_id", eventid.value);
        const res = await api.getInviteModules(fd);
        const payload = res?.data ?? res;
        const list = Array.isArray(payload?.data) ? payload.data : [];
        if (!list.length) return;

        const next = {
          textPhoto: false,
          schedule: false,
          poll: false,
          carPool: false,
        };

        list.forEach((item) => {
          const rawKey = String(item?.value || item?.id || "").trim().toLowerCase();
          const mapped = moduleKeyMap[rawKey];
          if (!mapped) return;
          next[mapped] = parseApiToggle(item?.status) ?? false;
        });

        attachments.value = next;
      } catch (e) {
        console.error("Failed to load invite modules:", e);
      }
    };

    /* ---------- Build payload dates ---------- */
    const buildDatesForPayload = () => {
      const today = new Date().toISOString().split("T")[0];
      let fromdate = start_date.value || today;

      // Prevent invalid ranges based on event type
      if (isNewEvent.value && fromdate < today) {
        fromdate = today;
      }
      if (isPastEvent.value && fromdate > today) {
        fromdate = today;
      }

      let todate = "";
      let fromtime = "";
      let totime = "";

      if (all_day.value && !include_end.value) {
        // Single all-day event
        todate = fromdate;

      } else if (all_day.value && include_end.value) {
        // Multi-day all-day event
        todate = end_date.value && end_date.value >= fromdate ? end_date.value : fromdate;

      } else if (!all_day.value && include_end.value) {
        // Multi-day timed event
        todate = end_date.value && end_date.value >= fromdate ? end_date.value : fromdate;
        fromtime = ensureAmPm(start_time.value);
        totime = ensureAmPm(end_time.value);

      } else if (!all_day.value && !include_end.value) {
        // Single date + single start time
        todate = fromdate;
        fromtime = ensureAmPm(start_time.value);
        totime = fromtime;
      }

      if (isPastEvent.value && todate && todate > today) {
        todate = today;
      }

      return { fromdate, todate, fromtime, totime };
    };

    // Prefill from API (PREVIEW STRICTLY FROM photos[])
    const hydrateFromApi = (info) => {
      if (!info) return;

      form.title = info.orgname || "";
      form.description = info.description || "";
      hostNameDisplay.value = info.hostname || hostNameDisplay.value;
      form.album_enabled = info.photo_booth === "yes";
      const apiFeedback = parseApiToggle(info.feedback);
      const apiRating = parseApiToggle(info.rating);
      form.feedback = apiFeedback ?? false;
      form.rating = form.feedback ? (apiRating ?? false) : false;
      attachments.value = {
        textPhoto: parseApiToggle(info.text_photo) ?? false,
        schedule: parseApiToggle(info.schedule) ?? false,
        poll: parseApiToggle(info.poll) ?? false,
        carPool: parseApiToggle(info.car_pool) ?? false,
      };
      const apiAcceptRsvp = parseApiToggle(info.rsvp_option);
      const apiGuestCount = parseApiToggle(info.rsvp_family);
      const apiCheckinOption = parseApiToggle(info.checkin_option);
      settings.acceptRsvp = apiAcceptRsvp ?? true;
      settings.guestCount = settings.acceptRsvp ? (apiGuestCount ?? false) : false;
      settings.checkinEnabled = apiCheckinOption ?? true;

      // Dates & times
      const fd = (info.fromdate || "").trim();
      const td = (info.todate || "").trim();
      const ft = normalizeAmPm((info.fromtime || "").trim());
      const tt = normalizeAmPm((info.totime || "").trim());

      start_date.value = fd || "";
      end_date.value = td || fd || "";
      start_time.value = ft || "";
      end_time.value = tt || "";

      const apiAllDay = parseApiToggle(info.all_day);
      const apiIncludeEnd = parseApiToggle(info.include_end_time ?? info.include_end);
      const derivedAllDay = !ft && !tt;
      const derivedIncludeEnd = ((!!td && td !== fd) || (!!tt)); // parentheses to avoid precedence issues

      all_day.value = apiAllDay != null ? apiAllDay : derivedAllDay;
      include_end.value = apiIncludeEnd != null ? apiIncludeEnd : derivedIncludeEnd;
      collapses.dateCollapse = true;

      // Location
      form.address = info.address || "";
      form.event_place = info.event_place || "";
      form.door_no = info.door_no || "";
      form.locality = info.locality || "";
      form.lat = info.lat || "";
      form.lng = info.lng || "";
      locationSearch.value = form.address;

      // Event type
      const apiTypeKey = String(info.event_type || info.types || "").trim().toLowerCase();
      pendingEventTypeKey.value = apiTypeKey || "";
      requiresEventTypeChoice.value = !apiTypeKey;
      if (apiTypeKey) resolveEventTypeFromKey();
      step.value = requiresEventTypeChoice.value ? "type" : "form";

      const parseYouTubeId = (url) => {
        if (!url) return null;
        const s = String(url).trim();
        const match = s.match(/(?:v=|\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
        return match ? match[1] : null;
      };

      // Build media slots from photos_new first (fallback to photos[] / p1..p3)
      const apiVideoLink = String(info.video_link || "").trim();
      const apiVideoThumb = String(info.video_thumb || "").trim();
      const ytId = parseYouTubeId(apiVideoLink);
      const hasVideoLink = !!apiVideoLink;
      const photosNew = Array.isArray(info.photos_new) ? info.photos_new : [];
      const orderedMedia = [];

      photosNew.forEach((entry) => {
        if (entry && typeof entry === "object") {
          const type = String(entry.type || "").trim().toLowerCase();
          const value = String(entry.value || "").trim();
          if (!value) return;
          if (type === "video") {
            orderedMedia.push({ kind: "video", value });
          } else {
            orderedMedia.push({ kind: "image", value });
          }
          return;
        }
        const value = String(entry || "").trim();
        if (value) orderedMedia.push({ kind: "image", value });
      });

      const normalizedMedia = [];
      if (orderedMedia.length) {
        let imageCount = 0;
        let videoAdded = false;
        orderedMedia.forEach((item) => {
          if (item.kind === "video") {
            if (!videoAdded && hasVideoLink) {
              normalizedMedia.push(item);
              videoAdded = true;
            }
            return;
          }
          if (imageCount < 3) {
            normalizedMedia.push(item);
            imageCount += 1;
          }
        });
      }

      if (!normalizedMedia.length) {
        const list = (Array.isArray(info.photos) && info.photos.length)
          ? info.photos
          : [info.p1_url, info.p2_url, info.p3_url];
        list.forEach((u) => {
          const value = String(u || "").trim();
          if (value) normalizedMedia.push({ kind: "image", value });
        });
      }

      const uniqueImageSet = new Set();
      const mediaImages = [];
      normalizedMedia.forEach((item) => {
        if (item.kind !== "image") return;
        const key = String(item.value || "").trim().toLowerCase();
        if (!key || uniqueImageSet.has(key)) return;
        uniqueImageSet.add(key);
        if (mediaImages.length < 3) mediaImages.push({ url: item.value, isRemote: true });
      });
      const videoSlotIndex = normalizedMedia.findIndex((item) => item.kind === "video");
      const photoNewVideoThumb = normalizedMedia.find((item) => item.kind === "video")?.value || "";
      const resolvedVideoThumb = String(
        photoNewVideoThumb ||
        apiVideoThumb ||
        (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : "")
      ).trim();
      const resolvedVideoSlot = videoSlotIndex >= 0
        ? videoSlotIndex
        : Math.min(mediaImages.length, 3);

      if (hasVideoLink) {
        coverMedia.value = {
          type: "youtube",
          images: mediaImages,
          video: null,
          youtube: {
            url: apiVideoLink,
            thumbnail: resolvedVideoThumb,
            thumbUrl: resolvedVideoThumb,
            slotIndex: resolvedVideoSlot
          }
        };
        return;
      }

      coverMedia.value = {
        type: "images",
        images: mediaImages,
        video: null,
        youtube: null
      };
    };

    const loadInvitation = async () => {
      if (!eventid.value) {
        alertErr("No event ID provided");
        return;
      }

      try {
        const fd = new FormData();
        fd.append("eventid", eventid.value);
        const { data } = await api.getInvitationDetail(fd);
        const pastFlagRaw = Number(data?.event_past_status)
        eventPastStatus.value = Number.isNaN(pastFlagRaw) ? null : pastFlagRaw
        const info = Array.isArray(data?.eventsinfo) ? data.eventsinfo[0] : data?.event || null;

        if (!info) {
          alertErr("Unable to load event details.");
          return;
        }

        hydrateFromApi(info);

        // cohosts come from ROOT (not eventsinfo)
        apiHostIdsCsv.value = (data?.cohost_ids || "").trim();
        apiCohostNamesCsv.value = (data?.cohost_names || "").trim();
        await loadInviteModules();

        await nextTick();
        initFlatpickr();
      } catch (e) {
        console.error("Failed to load invitation:", e);
        alertErr("Failed to load event details.");
      }
    };

    // Google Maps functions
    const onLocationSearch = () => {
      if (!autocompleteService.value || !locationSearch.value) {
        locationSuggestions.value = [];
        return;
      }
      autocompleteService.value.getPlacePredictions({ input: locationSearch.value }, (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
          locationSuggestions.value = preds;
        } else {
          locationSuggestions.value = [];
        }
      });
    };

    const selectLocation = (suggestion) => {
      locationSearch.value = suggestion.description;
      locationSuggestions.value = [];
      form.locality = suggestion.description || "";
      if (!placesService.value) return;

      placesService.value.getDetails({ placeId: suggestion.place_id }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          form.lat = place.geometry.location.lat();
          form.lng = place.geometry.location.lng();
          const fullAddress = String(place.formatted_address || suggestion.description || locationSearch.value || "").trim();
          locationSearch.value = fullAddress || locationSearch.value;
          form.address = fullAddress;
          form.locality = fullAddress;
          extractAddressComponents(place.address_components);
          if (!form.event_place && fullAddress) form.event_place = fullAddress.split(",")[0].trim();
        }
      });
    };

    const extractAddressComponents = (ac) => {
      if (!ac || !Array.isArray(ac)) return;

      const pick = (typeGroups = []) => {
        for (const comp of ac) {
          const types = comp?.types || [];
          if (typeGroups.some((t) => types.includes(t))) return comp.long_name;
        }
        return "";
      };

      const route = pick(["route"]);
      const sublocality = pick(["sublocality", "sublocality_level_1", "sublocality_level_2", "neighborhood"]);
      const locality = pick(["locality"]);
      const admin2 = pick(["administrative_area_level_2"]);
      const admin1 = pick(["administrative_area_level_1"]);

      const eventPlaceCandidate = sublocality || locality || admin2 || route || "";
      const localityCandidate = locality || admin2 || admin1 || "";

      if (eventPlaceCandidate) form.event_place = eventPlaceCandidate;
      if (!form.locality && localityCandidate) form.locality = localityCandidate;
    };

    const getCurrentLocation = () => {
      if (!navigator.geolocation) {
        alertErr("Geolocation is not supported by your browser");
        return;
      }
      gettingLocation.value = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          form.lat = lat;
          form.lng = lng;

          if (geocoder.value) {
            geocoder.value.geocode({ location: { lat, lng } }, (results, status) => {
              gettingLocation.value = false;
              if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
                const address = results[0].formatted_address;
                locationSearch.value = address;
                const fullAddress = String(address || locationSearch.value || "").trim();
                locationSearch.value = fullAddress || locationSearch.value;
                form.address = fullAddress;
                form.locality = fullAddress;
                extractAddressComponents(results[0].address_components);
                if (!form.event_place && fullAddress) form.event_place = fullAddress.split(",")[0].trim();
              } else {
                alertErr("Could not get address from your location");
              }
            });
          } else {
            gettingLocation.value = false;
          }
        },
        (err) => {
          gettingLocation.value = false;
          alertErr(`Error getting location: ${err.message}`);
        },
        { timeout: 10000 }
      );
    };

    const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

    // ------- Flatpickr wiring -------
    let flatpickrModule = null;
    let startDateFP = null;
    let endDateFP = null;
    let startTimeFP = null;
    let endTimeFP = null;

    const pickerInputIds = ['startDateInput', 'endDateInput', 'startTimeInput', 'endTimeInput']

    const destroyPickers = () => {
      [startDateFP, endDateFP, startTimeFP, endTimeFP].forEach((fp) => {
        if (fp && typeof fp.destroy === "function") fp.destroy();
      });
      startDateFP = endDateFP = startTimeFP = endTimeFP = null;
    };

    const setDateInputsDisabled = (disabled) => {
      pickerInputIds.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        if (disabled) {
          el.setAttribute('disabled', 'disabled');
          el.classList.add('is-past-locked');
        } else {
          el.removeAttribute('disabled');
          el.classList.remove('is-past-locked');
        }
      });
    };

    // Rebuild when DOM structure changes due to v-if toggles
    const rebuildPickersOnStructureChange = async () => {
      if (isEventPastLocked.value) {
        destroyPickers();
        await nextTick();
        setDateInputsDisabled(true);
        return;
      }
      const snap = {
        sd: start_date.value,
        ed: end_date.value,
        st: start_time.value,
        et: end_time.value,
      };
      destroyPickers();
      await nextTick();
      start_date.value = snap.sd || start_date.value;
      end_date.value   = snap.ed   || end_date.value || snap.sd || "";
      start_time.value = snap.st   || start_time.value || "12:00 PM";
      end_time.value   = snap.et   || end_time.value   || "12:00 PM";
      await initFlatpickr();
      applyMinDateByType();
      syncEndTimeConstraints();
    };

    // Keep end-time >= start-time when same day
    const syncEndTimeConstraints = () => {
      const sameDay = !!start_date.value && !!end_date.value && (start_date.value === end_date.value);
      if (startTimeFP?.input) startTimeFP.input.disabled = all_day.value;
      if (endTimeFP?.input) endTimeFP.input.disabled = all_day.value || !include_end.value;
      if (!endTimeFP) return;
      if (all_day.value || !include_end.value) {
        endTimeFP.set('minTime', null);
        return;
      }
      if (sameDay) {
        const minT = start_time.value || "12:00 PM";
        endTimeFP.set('minTime', minT);
        if (end_time.value && cmpTimes(end_time.value, minT) < 0) {
          end_time.value = minT;
          endTimeFP.setDate(minT, false, "h:i K");
        }
      } else {
        endTimeFP.set('minTime', null);
      }
    };

    // Init flatpickr
    const initFlatpickr = async () => {
      if (step.value !== "form") return;
      try {
        if (!flatpickrModule) {
          flatpickrModule = await import(/* webpackChunkName: "flatpickr" */ "flatpickr");
        }
        const flatpickr = flatpickrModule.default || flatpickrModule;

        await nextTick();
        destroyPickers();

        const startDateEl = document.getElementById("startDateInput");
        const endDateEl   = document.getElementById("endDateInput");
        const startTimeEl = document.getElementById("startTimeInput");
        const endTimeEl   = document.getElementById("endTimeInput");

        if (isEventPastLocked.value) {
          setDateInputsDisabled(true);
          return;
        }
        setDateInputsDisabled(false);

        const isNew = isNewEvent.value;
        const isPast = isPastEvent.value;
        const todayStr = new Date().toISOString().split("T")[0];
        const minStart = isNew ? todayStr : null;
        const maxStart = isPast ? todayStr : null;

        if (startDateEl) {
          startDateFP = flatpickr(startDateEl, {
            dateFormat: "Y-m-d",
            allowInput: true,
            disableMobile: true,
            defaultDate: start_date.value || (isNew ? todayStr : null),
            minDate: minStart,
            maxDate: maxStart,
            onChange: (dates) => {
              if (dates[0]) {
                const s = dates[0].toISOString().split("T")[0];
                start_date.value = s;
                if (endDateFP) {
                  endDateFP.set("minDate", s);
                  endDateFP.set("maxDate", isPast ? todayStr : null);
                }
                if (include_end.value && end_date.value && end_date.value < s) {
                  end_date.value = s;
                  endDateFP?.setDate(s, false);
                }
                syncEndTimeConstraints();
              }
            },
          });
        }

        if (endDateEl) {
          const minEnd = start_date.value || minStart;
          endDateFP = flatpickr(endDateEl, {
            dateFormat: "Y-m-d",
            allowInput: true,
            disableMobile: true,
            defaultDate: end_date.value || minEnd,
            minDate: minEnd,
            maxDate: isPast ? todayStr : null,
            onChange: (dates) => {
              if (dates[0]) {
                end_date.value = dates[0].toISOString().split("T")[0];
                syncEndTimeConstraints();
              }
            },
          });
        }

        if (startTimeEl) {
          startTimeEl.disabled = all_day.value;
          startTimeFP = flatpickr(startTimeEl, {
            enableTime: true,
            noCalendar: true,
            dateFormat: "h:i K",
            time_24hr: false,
            disableMobile: true,
            defaultDate: start_time.value || "12:00 PM",
            onChange: (_d, str) => {
              start_time.value = normalizeAmPm(str);
              syncEndTimeConstraints();
            },
          });
        }

        if (endTimeEl) {
          endTimeEl.disabled = all_day.value || !include_end.value;
          endTimeFP = flatpickr(endTimeEl, {
            enableTime: true,
            noCalendar: true,
            dateFormat: "h:i K",
            time_24hr: false,
            disableMobile: true,
            defaultDate: end_time.value || "12:00 PM",
            onChange: (_d, str) => {
              end_time.value = normalizeAmPm(str);
              syncEndTimeConstraints();
            },
          });
        }

        applyMinDateByType();
        syncEndTimeConstraints();
      } catch (error) {
        console.error("Failed to initialize date pickers:", error);
      }
    };

    // Keep constraints in sync
    const applyMinDateByType = () => {
      const todayStr = new Date().toISOString().split("T")[0];
      const minStart = isNewEvent.value ? todayStr : null;
      const maxStart = isPastEvent.value ? todayStr : null;
      const minEndDate = start_date.value || minStart;
      const maxEndDate = isPastEvent.value ? todayStr : null;

      if (startDateFP) {
        startDateFP.set("minDate", minStart);
        startDateFP.set("maxDate", maxStart);
      }
      if (start_date.value) {
        let adjusted = start_date.value;
        if (minStart && adjusted < minStart) adjusted = minStart;
        if (maxStart && adjusted > maxStart) adjusted = maxStart;
        if (adjusted !== start_date.value) {
          start_date.value = adjusted;
          startDateFP?.setDate(adjusted, false);
        }
      }

      if (endDateFP) {
        endDateFP.set("minDate", minEndDate);
        endDateFP.set("maxDate", maxEndDate);
      }
      if (include_end.value && end_date.value) {
        let adjustedEnd = end_date.value;
        if (minEndDate && adjustedEnd < minEndDate) adjustedEnd = minEndDate;
        if (maxEndDate && adjustedEnd > maxEndDate) adjustedEnd = maxEndDate;
        if (adjustedEnd !== end_date.value) {
          end_date.value = adjustedEnd;
          endDateFP?.setDate(adjustedEnd, false);
        }
      }
    };

    // Watchers
    watch(normalizedType, () => applyMinDateByType());

    watch(start_date, (newVal) => {
      if (include_end.value && end_date.value && newVal && end_date.value < newVal) {
        end_date.value = newVal;
        endDateFP?.setDate(newVal, false);
      }
      syncEndTimeConstraints();
      applyMinDateByType();
    });

    // Rebuild pickers when toggles change (fixes flatpickr teardown via v-if)
    watch(all_day, async () => {
      await rebuildPickersOnStructureChange();
    });

    watch(include_end, async () => {
      await rebuildPickersOnStructureChange();
    });

    watch(() => form.feedback, (val) => {
      if (!val) form.rating = false;
    });

    // Ensure we (re)init when entering the form step
    watch(step, (newVal) => {
      if (newVal === "form") {
        setTimeout(() => initFlatpickr(), 100);
      } else {
        destroyPickers();
      }
    });

    // Cancel button
    const cancelEdit = () => {
      router.push("/host");
    };

    // Mount (sequential to ensure cohost preselect determinism)
    onMounted(async () => {
      session.loadUser();

      // 1) Invitation (sets cohost ids/names from ROOT)
      await loadInvitation();

      // 2) Profile & event types
      await fetchProfile();
      await fetchEventTypes();

      // 3) Friends (cohost list)
      await getCoHosts();

      // 4) Now resolve preselection
      resolveCohostsFromApi();

      // Initialize services
      try {
        await loadGoogleMapsAPI();
        if (window.google?.maps) {
          autocompleteService.value = new window.google.maps.places.AutocompleteService();
          placesService.value = new window.google.maps.places.PlacesService(document.createElement("div"));
          geocoder.value = new window.google.maps.Geocoder();
        }
      } catch(e) {
        console.error('Google Maps initialization error:', e);
      }

      if (step.value === "form") {
        await nextTick();
        initFlatpickr();
      }
    });

    onBeforeUnmount(() => {
      destroyPickers();
    });

    // Route updates
    onBeforeRouteUpdate(async (to) => {
      const newEventId = deriveEventId(to);
      if (newEventId && newEventId !== eventid.value) {
        eventid.value = newEventId;
        await loadInvitation();
        resolveCohostsFromApi();
      }
    });

    watch(() => route.params.eventid, (newEventId) => {
      if (newEventId && newEventId !== eventid.value) {
        eventid.value = newEventId;
        loadInvitation().then(resolveCohostsFromApi);
      }
    });

    // If cohosts arrive later, try resolving again
    watch(cohosts, () => {
      if (cohosts.value.length > 0) {
        resolveCohostsFromApi();
      }
    });

    // Submit
    const submitForm = async () => {
      if (isSubmitting.value) return;

      // Basic validations
      if (!form.title.trim()) return alertErr("Please enter an event title");
      if (!start_date.value) return alertErr("Please select a start date");
      if (include_end.value && !end_date.value) return alertErr("Please select an end date");
      if (!form.address && !form.event_place && !form.locality) return alertErr("Please provide a location");
      if (requiresEventTypeChoice.value && !selectedEventType.value) return alertErr("Please select an event type");

      try {
        isSubmitting.value = true;

        const imageEntries = [];
        const urlImages = [];
        (coverMedia.value.images || []).forEach((img) => {
          const remoteUrl = String(img?.url || "").trim();
          if (img?.file instanceof File) {
            imageEntries.push({ kind: "file", file: img.file });
          } else if (remoteUrl) {
            imageEntries.push({ kind: "url", url: remoteUrl });
            urlImages.push(remoteUrl);
          }
        });

        if (imageEntries.length > 3) {
          return alertErr("Maximum 3 cover images are allowed");
        }

        const videoLinkUrl = String(coverMedia.value.youtube?.url || "").trim();
        const hasVideoLink = !!videoLinkUrl;
        const videoItem = !hasVideoLink ? coverMedia.value.video : null;
        const hasVideoSource = !!(videoItem?.file instanceof File || String(videoItem?.url || "").trim() || String(videoItem?.preview || "").trim());
        const hasVideoThumb = !!(videoItem?.thumbFile instanceof File || String(videoItem?.thumbUrl || "").trim() || String(videoItem?.thumbPreview || "").trim());
        const hasVideoLinkThumb = !!(
          coverMedia.value.youtube?.thumbFile instanceof File ||
          String(coverMedia.value.youtube?.thumbUrl || "").trim() ||
          String(coverMedia.value.youtube?.thumbPreview || "").trim() ||
          String(coverMedia.value.youtube?.thumbnail || "").trim()
        );
        if (hasVideoLink && !hasVideoLinkThumb) {
          return alertErr("Video thumbnail is mandatory. Please upload/select a thumbnail.");
        }
        if (videoItem && hasVideoSource && !hasVideoThumb) {
          return alertErr("Video thumbnail is mandatory. Please upload/select a thumbnail.");
        }
        const maxTotal = Math.min(3, imageEntries.length);
        const hasAnyVideo = hasVideoLink || (videoItem && hasVideoSource);
        const mediaSlotCount = maxTotal + (hasAnyVideo ? 1 : 0);
        const rawSlotIndex = hasVideoLink
          ? Number(coverMedia.value.youtube?.slotIndex)
          : Number(videoItem?.slotIndex);
        const safeVideoSlot = Number.isInteger(rawSlotIndex)
          ? Math.min(Math.max(rawSlotIndex, 0), Math.max(0, mediaSlotCount - 1))
          : Math.min(maxTotal, Math.max(0, mediaSlotCount - 1));
        const typeSlots = new Array(mediaSlotCount).fill("");
        if (hasAnyVideo && mediaSlotCount > 0) typeSlots[safeVideoSlot] = "video";
        let imagePtr = 0;
        for (let i = 0; i < mediaSlotCount && imagePtr < maxTotal; i++) {
          if (typeSlots[i]) continue;
          typeSlots[i] = "gallery";
          imagePtr++;
        }
        const typesCsv = typeSlots.filter(Boolean).join(",");
        const urlsToSend = [];
        let filesToSend = [];
        try {
          filesToSend = (await Promise.all(
            imageEntries.slice(0, maxTotal).map(async (entry, index) => {
              if (entry.kind === "file" && entry.file instanceof File) return entry.file;
              if (entry.kind === "url") return fetchUrlAsFile(entry.url, `remote_image_${index + 1}`);
              return null;
            })
          )).filter((file) => file instanceof File);
        } catch (err) {
          return alertErr(err?.message || "Unable to import one of the remote images as a file.");
        }

        const fd = new FormData();

        // identity
        fd.append("id", session.userId || "");
        fd.append("eventid", eventid.value || "");

        // basic info
        fd.append("orgname", form.title.trim());
        fd.append("hostname", hostNameDisplay.value || "");
        fd.append("description", form.description || "");

        // dates
        const { fromdate, todate, fromtime, totime } = buildDatesForPayload();
        fd.append("fromdate", fromdate); 
        fd.append("todate", todate || '');
        fd.append("fromtime", fromtime || '');
        fd.append("totime", totime || '');
        fd.append("all_day", all_day.value ? 'yes' : 'no');
        fd.append("include_end_time", include_end.value ? 'yes' : 'no');

        // location
        const fullAddress = String(
          form.address ||
          locationSearch.value ||
          `${form.event_place || ""}${form.locality ? (form.event_place ? ", " : "") + form.locality : ""}`
        ).trim();
        const addressStr = fullAddress;
        const localityStr = fullAddress || form.locality || "";
        fd.append("address", addressStr);
        fd.append("event_place", form.event_place || "");
        fd.append("door_no", form.door_no || "");
        fd.append("locality", localityStr);
        fd.append("lat", form.lat || "");
        fd.append("lng", form.lng || "");

        // types & flags
        fd.append("mode_id", "2");
        fd.append("type", "8");
        // fd.append("subtype", "");
        const resolvedType = selectedEventType.value?.type || pendingEventTypeKey.value || "";
        const eventType = requiresEventTypeChoice.value
          ? (resolvedType || "new")
          : resolvedType;
        fd.append("event_type", eventType);
        fd.append("photo_booth", form.album_enabled ? "yes" : "no");
        fd.append("feedback", toFlag(form.feedback));
        fd.append("rating", toFlag(form.feedback && form.rating));
        fd.append("rsvp_option", toFlag(settings.acceptRsvp));
        fd.append("rsvp_family", toFlag(settings.acceptRsvp && settings.guestCount));
        fd.append("car_pool", attachments.value.carPool ? "1" : "0");
        fd.append("poll", attachments.value.poll ? "1" : "0");
        fd.append("schedule", attachments.value.schedule ? "1" : "0");
        fd.append("text_photo", attachments.value.textPhoto ? "1" : "0");
        // fd.append("ask", "");

        // cohosts
        const hostIdsCsv = selectedCohosts.value.map((c) => c.id).join(",");
        fd.append("host_ids", hostIdsCsv);

        // defaults expected by API
        // fd.append("vendors_list", "no");
        fd.append("permission_id", "1");
        // fd.append("payment", "");
        // fd.append("members", "");
        // fd.append("name", "");
        // fd.append("names", "");
        // fd.append("codes", "");
        // fd.append("price", "");
        // fd.append("emails", "");
        // fd.append("phone", "");
        // fd.append("phones", "");
        fd.append("public_option", "0");
        fd.append("security_type", "private");
        fd.append("video", "");
        fd.append("types", typesCsv);
        fd.append("location_type", "1");
        fd.append("checkin_option", toFlag(settings.checkinEnabled));
        // fd.append("web_url", "");
        fd.set("p1_url", "");
        fd.set("p2_url", "");
        fd.set("p3_url", "");

        // URL images
        urlsToSend.forEach((url, idx) => {
          if (!url) return;
          fd.set(`p${idx + 1}_url`, url);
          fd.append("images[]", url);
        });

        // Local files
        filesToSend.forEach((file, idx) => {
          const fieldName = idx === 0 ? "img" : `img${idx}`;
          const name = file?.name || `image_${idx + 1}.jpg`;
          fd.append(fieldName, file, name);
          // Compatibility for backends expecting array-style image fields.
          fd.append("images[]", file, name);
        });

        if (hasVideoLink) {
          const ytThumbFile = coverMedia.value.youtube?.thumbFile;
          const ytThumbUrl = String(
            coverMedia.value.youtube?.thumbUrl ||
            coverMedia.value.youtube?.thumbPreview ||
            coverMedia.value.youtube?.thumbnail ||
            ""
          );
          fd.append("video_link", videoLinkUrl);
          fd.append("video_upload", "");
          if (ytThumbFile instanceof File) {
            fd.append("video_thumb", ytThumbFile, ytThumbFile.name || "video_thumb.jpg");
          } else {
            fd.append("video_thumb", ytThumbUrl);
          }
        } else if (videoItem && hasVideoSource) {
          if (videoItem.file instanceof File) {
            fd.append("video_upload", videoItem.file, videoItem.file.name || "cover_video.mp4");
          } else {
            fd.append("video_upload", String(videoItem.url || videoItem.preview || ""));
          }
          fd.append("video_link", "");
          if (videoItem.thumbFile instanceof File) {
            fd.append("video_thumb", videoItem.thumbFile, videoItem.thumbFile.name || "video_thumb.jpg");
          } else {
            fd.append("video_thumb", String(videoItem.thumbUrl || videoItem.thumbPreview || ""));
          }
        } else {
          fd.append("video_link", "");
          fd.append("video_upload", "");
          fd.append("video_thumb", "");
        }

        const res = await api.editInvitation(fd);

        if (res?.data?.status === "ok") {
          const eid = res?.data?.data?.event_id || eventid.value;
          toast(`Event updated successfully! (ID: ${eid})`);
        } else {
          const msg = res?.data?.msg || "Failed to update event.";
          const errors = res?.data?.errors;
          alertErr(errors ? `${msg}: ${errors.join(", ")}` : msg);
        }
      } catch (e) {
        console.error("Form submission error:", e);
        const apiMsg = e?.response?.data?.msg || e?.response?.data?.message;
        const errors = e?.response?.data?.errors;
        alertErr(apiMsg ? (errors ? `${apiMsg}: ${errors.join(", ")}` : apiMsg) : "Failed to update event.");
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      // state
      step, requiresEventTypeChoice, eventTypes, selectedEventType, loadingEventTypes,
      form, collapses, coverMedia, isSubmitting, formTitle,
      eventid,
      // dates
      all_day, include_end, start_date, start_time, end_date, end_time,
      // location
      locationSearch, locationSuggestions, showManualAddress, gettingLocation,
      
      settings, showSettingsModal,
      attachments,
      // computed
      isEventPastLocked,
      // cohosts/user
      cohosts, selectedCohosts, user, hostNameDisplay, showCohostModal, 
      // methods
      selectEventType, goToForm, toggleCollapse,
      onLocationSearch, selectLocation, getCurrentLocation,
      openCohostModal, closeCohostModal, saveCohostModal, toggleCohost, removeCohost, isCohostSelected,
      capitalize, submitForm,
      cancelEdit,
    };
  },
};
</script>

<style scoped>
/* Tiles & radio look */
.page-bar{
  border:1px solid #eef1f6;
  border-radius:14px;
  background:#fff;
  box-shadow:0 8px 20px rgba(15,23,42,.06);
  padding:.85rem 1rem;
}
.page-title{font-weight:700;font-size:1.05rem;}
.card{border-radius:14px;border:1px solid #f3e8ee;background:#fff;box-shadow:0 12px 26px rgba(15,23,42,.06);}
.btn-primary{border-radius:10px;font-weight:700;}
.btn-outline-primary{border-radius:10px;font-weight:700;}
.chip-btn{
  border-radius:999px;
  min-height:36px;
  padding:.45rem .95rem;
  font-weight:700;
  font-size:.92rem;
  line-height:1.1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:.35rem;
  white-space:nowrap;
}
.chip-actions .address-chip{
  border-radius:999px;
  padding:.45rem .9rem;
  font-weight:700;
  font-size:.92rem;
  line-height:1.1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:.35rem;
  box-shadow:0 10px 20px rgba(15,23,42,.08);
}
.chip-actions .btn-outline-primary.address-chip{box-shadow:none;}
@media (max-width: 576px){
  .chip-actions{gap:.5rem;}
  .chip-actions .col-6{flex:0 0 100%;max-width:100%;}
  .chip-actions .address-chip{width:100%;font-size:.9rem;}
}
.collapse.show{display:block;}
.upload-tile{
  border:2px dashed #cbd5e1;
  background:#f8fafc;
  border-radius:14px;
  padding:1rem;
  cursor:pointer;
  font-weight:700;
  color:#0f172a;
  transition:all .15s ease;
}
.upload-tile:hover{background:#eef2f7;border-color:#94a3b8;}
.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;}

.event-settings-card{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 14px;
  border:1px solid #f1f5f9;
  border-radius:14px;
  background:#fff;
  box-shadow:0 12px 26px rgba(15,23,42,.06);
  cursor:pointer;
  transition:all .12s ease;
}
.event-settings-card:hover{box-shadow:0 14px 28px rgba(15,23,42,.08);}

.settings-modal-card{
  width:min(720px, 100%);
  background:#fff;
  border-radius:14px;
  box-shadow:0 26px 64px rgba(15,23,42,.22);
  max-height:85vh;
  overflow:auto;
}
.settings-backdrop{align-items:flex-end;}
.settings-modal-header{
  display:flex;
  align-items:center;
  padding:12px 16px;
  border-bottom:1px solid #f1f5f9;
  position:sticky;
  top:0;
  background:#fff;
  z-index:2;
}
.settings-modal-body{padding:16px 16px 22px; display:flex; flex-direction:column; gap:16px; background:#f7f1f5;}
.settings-section{
  padding:16px;
  border-radius:14px;
  border:1px solid #f3e8ee;
  background:linear-gradient(180deg,#fff7f8 0%, #ffffff 70%);
  box-shadow:0 12px 26px rgba(15,23,42,.06);
}
.settings-section-title{font-weight:700;color:#0f172a;font-size:1rem;line-height:1.3;}
.settings-section-sub{margin:0 0 10px;color:#64748b;font-size:.9rem;line-height:1.4;}
.settings-tile{
  display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap;
  background:#fff; border:1px solid #f1f5f9; border-radius:14px; padding:12px 14px;
  margin-bottom:12px;
}
.settings-tile:last-child{margin-bottom:0;}
.settings-tile > div:first-child{flex:1; min-width:200px;}
.settings-tile .switch{flex-shrink:0;}
.settings-tile-title{font-weight:700;color:#0f172a;font-size:1rem;line-height:1.3;}
.settings-tile-sub{color:#64748b;font-size:.9rem;line-height:1.4;margin-bottom:0;}
.disabled-tile{opacity:.6;}
.settings-modal-card .switch{margin-left:8px;}
@media (max-width: 768px){
  .settings-modal-card{border-radius:14px 14px 0 0;}
  .settings-tile{flex-direction:column; align-items:flex-start;}
}

.event-type-tile{
  background:#f8fbff;
  border:1.5px solid #cfe3ff;
  border-radius:12px;
  padding:16px;
  transition:all .15s ease;
  width:100%;
}
.event-type-tile__body{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:.75rem;
  flex-wrap:wrap;
}
.event-type-tile__copy{
  flex:1;
  min-width:0;
}
.event-type-tile__copy .h5,
.event-type-tile__copy .small{
  word-break:break-word;
}
.event-type-tile__body .radio-badge{
  flex-shrink:0;
}
@media (max-width: 768px){
  .event-type-tile__body{
    gap:.5rem;
  }
  .event-type-tile__copy{
    min-width:60%;
  }
}
.event-type-tile:hover{ box-shadow:0 6px 20px rgba(16,24,40,.06); }
.event-type-tile.active{ background:#eaf2ff; border-color:#6ea8fe; }
.radio-badge{
  width:24px; height:24px; border:2px solid #8bb3ff; border-radius:50%;
  display:inline-flex; align-items:center; justify-content:center; background:#fff;
}
.radio-badge .dot{ width:12px; height:12px; background:#2c6bed; border-radius:50%; }

/* Tabs */
.tabset{ display:flex; gap:8px; border-bottom:1px solid #e9ecef; margin-bottom:1rem; flex-wrap:wrap; }
.tab-btn{ appearance:none; border:0; background:transparent; padding:.5rem .75rem; border-radius:.25rem .25rem 0 0; font-weight:600; color:#6c757d; cursor:pointer; }
.tab-btn.active{ color:#212529; background:#f8f9fa; border:1px solid #e9ecef; border-bottom-color:transparent; }

/* Toggle switch */
.switch{ position:relative; display:inline-block; width:46px; height:26px; }
.switch input{ opacity:0; width:0; height:0; }
.slider{
  position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0;
  background-color:#e2e8f0; transition:.2s; border-radius:999px;
  box-shadow:none;
}
.slider:before{
  position:absolute; content:""; height:20px; width:20px; left:3px; bottom:3px;
  background:#fff; transition:.2s; border-radius:50%;
  box-shadow:0 4px 10px rgba(15,23,42,.15);
}
input:checked + .slider{ background:#f472b6; box-shadow:none; }
input:checked + .slider:before{ transform:translateX(20px); }
.switch input:focus-visible + .slider{
  outline:2px solid #f9a8d4;
  outline-offset:2px;
}
.disabled-tile .slider{ background:#e5eaf1; box-shadow:none; }

/* Chips & lists */
.category-tabs::-webkit-scrollbar { height:5px; }
.category-tabs::-webkit-scrollbar-thumb { background:#ccc; border-radius:10px; }
.cohost-chip{ max-width:210px; background-color:#cecece; border-radius:50px; }
.del-btn{ top:0; right:0; height:20px!important; width:20px!important; border-radius:50%; line-height:7px; padding:2px; }

/* Modal */
.modal-backdrop-custom{
  position:fixed; inset:0; background:rgba(15,23,42,.4); z-index:1050;
  display:flex; align-items:center; justify-content:center; padding:12px;
}
.modal-card{
  width:min(680px, 100%); background:#fff; border-radius:12px; box-shadow:0 24px 60px rgba(2,6,23,.25);
  max-height:80vh; overflow:auto;
}
.modal-header, .modal-footer{ padding:12px 16px; border-bottom:1px solid #eee; }
.modal-footer{ border-top:1px solid #eee; border-bottom:0; }
.modal-title{ margin:0; }
.close{ border:0; background:transparent; font-size:24px; line-height:1; }

/* Image slots */
.slot-tile{
  width:130px; height:130px; background:#f8f9fa; border:1px dashed #ced4da; position:relative;
  display:flex; align-items:center; justify-content:center; border-radius:8px; overflow:hidden;
}
.slot-actions{
  position:absolute; bottom:6px; left:6px; right:6px; display:flex; justify-content:space-between;
}
.btn-xs{ padding:2px 6px; font-size:12px; line-height:1.2; }
</style>
